import { AxiosError } from "axios";

import {
  DAILY_TODO_ERROR_CODES,
  GetDailyTodosResponseI,
} from "@/api/routes/daily-todos";

/** glen's NoRoute answer: what every To-Dos route says while switched off. */
const SWITCHED_OFF_ERROR_CODE = 1035;

export const DailyTodosQueryKeys = {
  all: () => ["daily-todos"] as const,
  switch: () => ["daily-todos-switch"] as const,
};

/** The caller's To-Dos, or `isSwitchedOn: false` while the backend switch is off. */
export interface DailyTodosDataI {
  isSwitchedOn: boolean;
  todos: GetDailyTodosResponseI | null;
  /** Server clock minus this browser's, so the gate and snooze use the server's time. */
  clockOffsetMs: number;
}

const readErrorCode = (error: unknown): number | undefined => {
  const code = (error as AxiosError<{ error_code?: number }>)?.response?.data
    ?.error_code;

  return typeof code === "number" ? code : undefined;
};

/** The switch is off (or the route isn't deployed yet): an answer, not an error. */
export const isDailyTodosSwitchedOff = (error: unknown): boolean =>
  (error as AxiosError)?.response?.status === 404 &&
  readErrorCode(error) === SWITCHED_OFF_ERROR_CODE;

/** The server refused the daily time or the time zone (1313). */
export const isBadDailyTodoSettings = (error: unknown): boolean =>
  readErrorCode(error) === DAILY_TODO_ERROR_CODES.BAD_SETTINGS;

/** A To-Dos refusal in plain words, keyed on the backend's error_code. */
export const describeDailyTodosFailure = (
  error: unknown,
  fallback: string
): string => {
  switch (readErrorCode(error)) {
    case DAILY_TODO_ERROR_CODES.BAD_SNOOZE:
      return "That snooze time isn't open any more. Pick another.";
    case DAILY_TODO_ERROR_CODES.MUST_FINISH:
      return "Finish today's to-dos first.";
    case DAILY_TODO_ERROR_CODES.CARD_NOT_FOUND:
      return "That to-do isn't on today's list any more.";
    case DAILY_TODO_ERROR_CODES.BAD_SETTINGS:
      return "That daily time or time zone isn't accepted. Pick another time.";
    case 1239:
      return "Changes are turned off while you're impersonating someone.";
  }

  return fallback;
};
